import { AIR, BEDROCK, CHUNK_VOLUME, fbm2, hash32, Rng, type VoxelWorld } from '@vm/shared';
import { family, M } from '../palette';

/** A material, or a function choosing one per voxel (patterns, noise). */
export type Brush = number | ((x: number, y: number, z: number) => number);

export const pick = (shades: number[], h: number): number => shades[h % shades.length];

// Brushes ------------------------------------------------------------------------------

/**
 * Patterned surfaces (bricks, planks, stone blocks) are drawn by the world shader, so these
 * brushes return one constant material per call. Keeping surfaces uniform lets the greedy
 * mesher merge whole walls into a handful of quads. The seed picks a shade for variety
 * between buildings.
 */
export function bricks(shades: number[], seed = 1): Brush {
  return shades[seed % shades.length];
}

export function planks(shades: number[], seed = 2): Brush {
  return shades[seed % shades.length];
}

export function floorBoards(shades: number[], seed = 3): Brush {
  return shades[seed % shades.length];
}

export function blocks(shades: number[], _size = 3, seed = 4): Brush {
  return shades[seed % shades.length];
}

/** Per-voxel random shade (use sparingly: it prevents face merging). */
export function speckle(shades: number[], seed = 5): Brush {
  const hash = hash32;
  return (x, y, z) => shades[hash(x, y, z, seed) % shades.length];
}

/** Uniform shade: the shader's per-voxel hash already adds subtle variation. */
export function solid(shades: number[], seed = 0): Brush {
  return shades[seed % shades.length];
}

/** Low-frequency patches of shades (grass, dirt, sand). */
export function patches(shades: number[], scale = 0.12, seed = 6): Brush {
  const noise = fbm2;
  return (x, _y, z) => {
    const n = noise(x * scale, z * scale, seed, 2);
    const i = Math.min(shades.length - 1, Math.floor(n * shades.length * 1.3 - shades.length * 0.15));
    return shades[Math.max(0, i)];
  };
}

export function stripes(a: number, b: number, width: number, axis: 'x' | 'y' | 'z' = 'y'): Brush {
  return (x, y, z) => {
    const v = axis === 'x' ? x : axis === 'y' ? y : z;
    return Math.floor(v / width) % 2 === 0 ? a : b;
  };
}

export const brushAt = (b: Brush, x: number, y: number, z: number): number => (typeof b === 'number' ? b : b(x, y, z));

// Symmetry -------------------------------------------------------------------------------

/**
 * How `MapBuilder.symmetrize` completes a map from its low half:
 * mirrorX / mirrorZ reflect the half x < sx/2 (z < sz/2) across the center line;
 * rotateX / rotateZ copy that half rotated 180 degrees about the map center.
 */
export type Symmetry = 'mirrorX' | 'mirrorZ' | 'rotateX' | 'rotateZ';

/** Material pairs exchanged when one team's half is copied to the other side. */
export function teamSwaps(extra: [string, string][] = []): [number, number][] {
  const pairs: [string, string][] = [['team_red', 'team_blue'], ['team_red_glow', 'team_blue_glow'], ['core_red', 'core_blue'], ...extra];
  return pairs.map(([a, b]) => [M(a), M(b)]);
}

/** Symmetric counterpart of a point in meters on a map of size sxm x szm meters. */
export function symPoint(kind: Symmetry, sxm: number, szm: number, x: number, z: number): [number, number] {
  if (kind === 'mirrorX') return [sxm - x, z];
  if (kind === 'mirrorZ') return [x, szm - z];
  return [sxm - x, szm - z];
}

/** Yaw (radians, 0 = facing -Z) of a spawn after applying the symmetry. */
export function symYaw(kind: Symmetry, yaw: number): number {
  if (kind === 'mirrorX') return -yaw;
  if (kind === 'mirrorZ') return Math.PI - yaw;
  return yaw + Math.PI;
}

/** Yaw that faces from (x, z) toward (tx, tz), in meters. */
export function yawToward(x: number, z: number, tx: number, tz: number): number {
  return Math.atan2(-(tx - x), -(tz - z));
}

// Terrain --------------------------------------------------------------------------------

/**
 * Samples `fn` every `step` voxels over an sx x sz area and returns a bilinear lookup: smooth
 * noise fields cost a fraction of evaluating fbm per column.
 */
export function smoothField(sx: number, sz: number, step: number, fn: (x: number, z: number) => number): (x: number, z: number) => number {
  const gx = Math.floor(sx / step) + 2;
  const gz = Math.floor(sz / step) + 2;
  const g = new Float64Array(gx * gz);
  for (let j = 0; j < gz; j++) for (let i = 0; i < gx; i++) g[i + j * gx] = fn(i * step, j * step);
  return (x, z) => {
    const fx = x / step;
    const fz = z / step;
    const i = Math.max(0, Math.min(gx - 2, Math.floor(fx)));
    const j = Math.max(0, Math.min(gz - 2, Math.floor(fz)));
    const tx = fx - i;
    const tz = fz - j;
    const k = i + j * gx;
    const top = g[k] + (g[k + 1] - g[k]) * tx;
    const bot = g[k + gx] + (g[k + gx + 1] - g[k + gx]) * tx;
    return top + (bot - top) * tz;
  };
}

export interface TerrainSpec {
  /** First air y per column (index x + z * sx). Columns <= 0 hold no ground, only water (if any). */
  height: Int16Array;
  /** Top voxel material: one for every column, or one per column. */
  surface: number | Uint8Array;
  /** Material under the surface; a list is picked per voxel by hash (only visible once dug). */
  sub: number | number[];
  /** Per-column sub material (overrides `sub`). */
  subCol?: Uint8Array;
  deep: number;
  subDepth?: number;
  /** Indestructible floor under every ground column (default true). */
  bedrock?: boolean;
  /** First y above the bedrock (default 2: bedrock is y 0-1). */
  bedrockTop?: number;
  /** Only columns higher than this get bedrock (default 0). */
  bedrockMin?: number;
  water?: number;
  /** First air y above the water. */
  waterLevel?: number;
}

// Builder --------------------------------------------------------------------------------

export type Side = 'n' | 's' | 'e' | 'w';

/**
 * A staircase as built: first step cell (x, z) at base y, climbing toward `dir`. `steps` is the number of steps. Older flights are one voxel
 * high and one deep per step (45 degrees); newer ones set `rise` and `tread` (voxels per step) and `height` (the whole climb): the last step is
 * always full height, the first may be lower.
 */
export interface StairRecord {
  x: number;
  y: number;
  z: number;
  dir: Side;
  width: number;
  steps: number;
  rise?: number;
  tread?: number;
  height?: number;
  /** The flight starts on a landing it is reached from (the second half of a switchback), so it has no foot of its own. */
  landing?: boolean;
}

export const stairRise = (r: StairRecord): number => r.rise ?? 1;
export const stairTread = (r: StairRecord): number => r.tread ?? 1;
/** Whole climb in voxels. */
export const stairHeight = (r: StairRecord): number => r.height ?? r.steps * stairRise(r);
/** Length of the flight along its direction, voxels. */
export const stairRun = (r: StairRecord): number => r.steps * stairTread(r);
/** Top of step i, voxels. */
export const stairStepTop = (r: StairRecord, i: number): number => r.y + stairHeight(r) - stairRise(r) * (r.steps - 1 - i);

/** A wall opening as cut (voxel box, half-open): kind tells doors (people walk through) from windows (sight and light). */
export interface OpeningRecord {
  kind: 'door' | 'window';
  /** Wall the opening is cut in; the inside of the building is the opposite way. */
  side: Side;
  x0: number;
  y0: number;
  z0: number;
  x1: number;
  y1: number;
  z1: number;
}

/** Debug hook: while `on`, every door and window cut by building() is recorded (stairs audit). */
export const OPENING_AUDIT: { on: boolean; list: OpeningRecord[] } = { on: false, list: [] };

/** Half-open rectangle in voxel plan coordinates. */
export interface Rect {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
}

/** A flight of building() stairs as planned: where the steps are and what must stay clear round them. */
export interface Flight {
  rec: StairRecord;
  /** Floor the flight starts on (the top floor for a roof flight). */
  floor: number;
  roof: boolean;
  /** Plan of the steps. */
  steps: Rect;
  /** Clear floor in front of the first step. */
  foot: Rect;
  /** Floor beyond the last step, where you step off. */
  exit: Rect;
  /** Opening cut in the slab above (the steps plus a little behind the first one, for head room). */
  open: Rect;
  /** Wall the flight runs along. */
  wall: Side;
  /** Solid platform at the turn of a switchback (voxel plan rect) and the height it rises to above the floor; the second flight starts on it. */
  platform?: { rect: Rect; top: number };
}

/**
 * Stair layout rules (voxels). Rise 0.2 m and tread 0.3 m is a 34 degree slope (ordinary house stairs are about 33; the old 0.1 / 0.1 was 45 and read
 * as a ladder). The foot is the clear floor in front of the first step: 2.0 m is more than three body widths, so you walk up to the stairs instead of
 * squeezing in. The landing is the floor you arrive on beyond the last step.
 */
export const STAIR_RISE = 2;
export const STAIR_TREAD = 3;
export const STAIR_FOOT = 20;
export const STAIR_TOP = 10;
export const STAIR_WIDTH = 12;
/** Switchback (U-turn) stairs for rooms too short for a straight flight: two lanes this wide, a landing this deep at the turn. */
export const STAIR_LANE = 10;
export const STAIR_LAND = 10;
const STAIR_BEHIND = 3;
/** Door zone: the floor this deep inside a doorway must stay free of stairs. */
export const DOOR_ZONE = 12;

/** Debug hook (tools and tests): while `lenient`, a building whose stairs do not fit is recorded here and built without them, instead of throwing. */
export const STAIR_PLAN: { lenient: boolean; misfits: { x0: number; z0: number; x1: number; z1: number; floors: number; floor: number; need: number; have: number; across: number }[] } = { lenient: false, misfits: [] };

const rectsTouch = (a: Rect, b: Rect): boolean => a.x0 < b.x1 && a.x1 > b.x0 && a.z0 < b.z1 && a.z1 > b.z0;

/** Debug hook: while `on`, every staircase built is recorded (tools/stairs-audit.ts). */
export const STAIR_AUDIT: { on: boolean; list: StairRecord[] } = { on: false, list: [] };

/**
 * The copy `symmetrize` makes of a staircase lying wholly in the source half (x or z below the
 * middle), or null for one that is not copied.
 */
function mirrorStair(r: StairRecord, flipX: boolean, flipZ: boolean, alongX: boolean, sx: number, sz: number): StairRecord | null {
  const ew = r.dir === 'e' || r.dir === 'w';
  // Largest cell coordinate of the flight along the split axis.
  const run = stairRun(r);
  const xb = ew ? (r.dir === 'e' ? r.x + run - 1 : r.x) : r.x + r.width - 1;
  const zb = ew ? r.z + r.width - 1 : r.dir === 's' ? r.z + run - 1 : r.z;
  if (alongX ? xb >= sx / 2 : zb >= sz / 2) return null;
  const flip: Record<Side, Side> = { e: 'w', w: 'e', n: 's', s: 'n' };
  let { x, z, dir } = r;
  if (flipX) {
    if (ew) {
      x = sx - 1 - r.x;
      dir = flip[dir];
    } else x = sx - r.x - r.width;
  }
  if (flipZ) {
    if (!ew) {
      z = sz - 1 - r.z;
      dir = flip[dir];
    } else z = sz - r.z - r.width;
  }
  return { x, y: r.y, z, dir, width: r.width, steps: r.steps, rise: r.rise, tread: r.tread, height: r.height, landing: r.landing };
}

export interface WindowSpec {
  w: number;
  h: number;
  sill: number;
  spacing: number;
  glass: number | null;
}

export interface DoorSpec {
  side: Side;
  /** Offset along the wall from its start, voxels; defaults to centered. */
  at?: number;
  w?: number;
  h?: number;
}

export interface BuildingSpec {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  y0: number;
  floors: number;
  floorH?: number;
  wallT?: number;
  wall: Brush;
  floor?: Brush;
  trim?: Brush;
  roof?: Brush;
  roofStyle?: 'flat' | 'gable' | 'gableX' | 'none';
  windows?: WindowSpec | null;
  doors?: DoorSpec[];
  stairs?: boolean;
  /** Flat roofs only: also build a flight from the top floor up through the roof slab onto the roof deck. */
  roofAccess?: boolean;
  /** Extra decorative band at each floor line. */
  band?: Brush;
  parapet?: number;
}

export class MapBuilder {
  readonly rng: Rng;
  constructor(
    readonly w: VoxelWorld,
    readonly seed: number,
  ) {
    this.rng = new Rng(seed);
  }

  get sx(): number {
    return this.w.sx;
  }
  get sy(): number {
    return this.w.sy;
  }
  get sz(): number {
    return this.w.sz;
  }

  set(x: number, y: number, z: number, b: Brush): void {
    this.w.set(x, y, z, brushAt(b, x, y, z));
  }

  /** Half-open box fill. */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush): void {
    if (typeof b === 'number') {
      this.w.fillBox(x0, y0, z0, x1, y1, z1, b);
      return;
    }
    x0 = Math.max(0, x0);
    y0 = Math.max(0, y0);
    z0 = Math.max(0, z0);
    x1 = Math.min(this.sx, x1);
    y1 = Math.min(this.sy, y1);
    z1 = Math.min(this.sz, z1);
    for (let y = y0; y < y1; y++) for (let z = z0; z < z1; z++) for (let x = x0; x < x1; x++) this.w.set(x, y, z, b(x, y, z));
  }

  clear(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void {
    this.w.fillBox(x0, y0, z0, x1, y1, z1, AIR);
  }

  /** Only fills cells that are currently air. */
  boxIfAir(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush): void {
    for (let y = Math.max(0, y0); y < Math.min(this.sy, y1); y++)
      for (let z = Math.max(0, z0); z < Math.min(this.sz, z1); z++)
        for (let x = Math.max(0, x0); x < Math.min(this.sx, x1); x++) if (this.w.get(x, y, z) === AIR) this.w.set(x, y, z, brushAt(b, x, y, z));
  }

  /** Four walls of thickness t around a half-open footprint. */
  walls(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, t: number, b: Brush): void {
    this.box(x0, y0, z0, x1, y1, z0 + t, b);
    this.box(x0, y0, z1 - t, x1, y1, z1, b);
    this.box(x0, y0, z0 + t, x0 + t, y1, z1 - t, b);
    this.box(x1 - t, y0, z0 + t, x1, y1, z1 - t, b);
  }

  cylinder(cx: number, cz: number, y0: number, y1: number, r: number, b: Brush, inner = -1): void {
    const r2 = r * r;
    const i2 = inner >= 0 ? inner * inner : -1;
    const R = Math.ceil(r);
    for (let dz = -R; dz <= R; dz++) {
      for (let dx = -R; dx <= R; dx++) {
        const d2 = dx * dx + dz * dz;
        if (d2 > r2 || d2 < i2) continue;
        for (let y = y0; y < y1; y++) this.set(cx + dx, y, cz + dz, b);
      }
    }
  }

  sphere(cx: number, cy: number, cz: number, r: number, b: Brush, onlyAir = false): void {
    const r2 = r * r;
    const R = Math.ceil(r);
    for (let dy = -R; dy <= R; dy++)
      for (let dz = -R; dz <= R; dz++)
        for (let dx = -R; dx <= R; dx++) {
          if (dx * dx + dy * dy + dz * dz > r2) continue;
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          if (onlyAir && this.w.get(x, y, z) !== AIR) continue;
          this.set(x, y, z, b);
        }
  }

  /** Ellipsoid with a noisy surface (tree canopies, boulders). */
  blob(cx: number, cy: number, cz: number, rx: number, ry: number, rz: number, b: Brush, noise = 0.25, onlyAir = true): void {
    const seed = this.rng.u32();
    const hash = hash32;
    const air = AIR;
    for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++)
      for (let dz = -Math.ceil(rz); dz <= Math.ceil(rz); dz++)
        for (let dx = -Math.ceil(rx); dx <= Math.ceil(rx); dx++) {
          const d = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) + (dz * dz) / (rz * rz);
          if (d > 1) continue;
          const x = cx + dx;
          const y = cy + dy;
          const z = cz + dz;
          const n = (hash(x, y, z, seed) / 4294967296) * noise;
          if (d > 1 - n) continue;
          if (onlyAir && this.w.get(x, y, z) !== air) continue;
          this.set(x, y, z, b);
        }
  }

  /** Bedrock floor plus layered ground up to `top` (exclusive). */
  ground(top: number, surface: Brush, sub: Brush, deep: Brush): void {
    this.box(0, 0, 0, this.sx, 2, this.sz, BEDROCK);
    this.box(0, 2, 0, this.sx, Math.max(2, top - 6), this.sz, deep);
    this.box(0, Math.max(2, top - 6), 0, this.sx, top - 1, this.sz, sub);
    this.box(0, top - 1, 0, this.sx, top, this.sz, surface);
  }

  /** Terrain from a height function, filling columns above `base` (layered materials). */
  terrain(base: number, height: (x: number, z: number) => number, surface: Brush, sub: Brush, subDepth = 5): void {
    for (let z = 0; z < this.sz; z++) {
      for (let x = 0; x < this.sx; x++) {
        const h = Math.floor(height(x, z));
        if (h <= base) continue;
        for (let y = base; y < h; y++) this.set(x, y, z, y >= h - 1 ? surface : y >= h - subDepth ? sub : sub);
      }
    }
  }

  /** Solid 45-degree staircase: `steps` steps along +x (dir 1) or -x (dir -1), 1 voxel rise per voxel. */
  stairsX(x: number, y: number, z0: number, z1: number, steps: number, dir: 1 | -1, b: Brush): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x, y, z: z0, dir: dir === 1 ? 'e' : 'w', width: z1 - z0, steps });
    for (let i = 0; i < steps; i++) {
      const xx = x + i * dir;
      this.box(xx, y, z0, xx + 1, y + i + 1, z1, b);
    }
  }

  stairsZ(x0: number, x1: number, y: number, z: number, steps: number, dir: 1 | -1, b: Brush): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x: x0, y, z, dir: dir === 1 ? 's' : 'n', width: x1 - x0, steps });
    for (let i = 0; i < steps; i++) {
      const zz = z + i * dir;
      this.box(x0, y, zz, x1, y + i + 1, zz + 1, b);
    }
  }

  /** A multi-story building with windows, doors, interior stairs and a roof. */
  building(s: BuildingSpec): { top: number; flights: Flight[] } {
    const floorH = s.floorH ?? 30;
    const t = s.wallT ?? 2;
    const floorB = s.floor ?? floorBoards(family('planks'));
    const { x0, z0, x1, z1, y0 } = s;
    const top = y0 + floorH * s.floors;

    // Clear the interior volume first so buildings can be placed over terrain.
    this.clear(x0, y0 + 1, z0, x1, top + 1, z1);
    // Plan the stairs before cutting windows so that no window is cut where a flight rises against the wall.
    const flights: Flight[] = (s.stairs ?? true) ? this.planStairs(s, t, floorH) : [];
    // A door that would open onto the first steps slides along its wall to the nearest clear spot.
    const doors = this.slideDoors(s, flights, t);
    // Foundation.
    this.box(x0, y0 - 2, z0, x1, y0 + 1, z1, s.trim ?? floorB);

    for (let f = 0; f < s.floors; f++) {
      const fy = y0 + f * floorH;
      if (f > 0) this.box(x0, fy - 1, z0, x1, fy + 1, z1, floorB);
      this.walls(x0, fy + 1, z0, x1, fy + floorH, z1, t, s.wall);
      if (s.band) {
        this.walls(x0, fy, z0, x1, fy + 1, z1, t, s.band);
      }
      // Windows.
      const win = s.windows === undefined ? { w: 8, h: 10, sill: 9, spacing: 18, glass: M('glass') } : s.windows;
      if (win) this.cutWindows(x0, z0, x1, z1, fy + 1 + win.sill, win, t, f === 0 ? doors : [], flights.filter((fl) => fl.floor === f));
    }

    // Doors (ground floor).
    for (const d of doors) this.cutDoor(x0, z0, x1, z1, y0 + 1, d, t);

    // Stairs: a flight between each pair of floors (and up to a flat roof when asked); each opens the slab above.
    for (const fl of flights) {
      const r = fl.rec;
      const built = this.flight(r.x, r.y, r.z, r.dir, r.width, stairHeight(r), floorB, stairRise(r), stairTread(r));
      built.landing = r.landing;
      if (fl.platform) {
        const p = fl.platform;
        this.box(p.rect.x0, r.y, p.rect.z0, p.rect.x1, r.y + p.top, p.rect.z1, floorB);
      }
      if (r.landing) {
        // Lane B stands on the landing's level: fill under it down to the floor so it is solid, not floating.
        const sr = fl.steps;
        this.box(sr.x0, y0 + fl.floor * floorH + 1, sr.z0, sr.x1, r.y, sr.z1, floorB);
      }
      if (!fl.roof) {
        const slabY = y0 + (fl.floor + 1) * floorH - 1;
        this.clear(fl.open.x0, slabY, fl.open.z0, fl.open.x1, slabY + 2, fl.open.z1);
      }
    }

    // Roof.
    const roofB = s.roof ?? bricks(family('roof_red'));
    const style = s.roofStyle ?? 'gable';
    if (style === 'flat' || style === 'none') {
      if (style === 'flat') {
        this.box(x0, top, z0, x1, top + 2, z1, s.trim ?? roofB);
        const p = s.parapet ?? 4;
        if (p > 0) this.walls(x0, top + 2, z0, x1, top + 2 + p, z1, 1, s.trim ?? s.wall);
        for (const fl of flights) if (fl.roof) this.clear(fl.open.x0, top, fl.open.z0, fl.open.x1, top + 2, fl.open.z1);
      }
      return { top: top + 2, flights };
    }
    this.box(x0, top, z0, x1, top + 1, z1, floorB);
    const alongX = style === 'gable' ? x1 - x0 >= z1 - z0 : false;
    const span = alongX ? z1 - z0 : x1 - x0;
    const layers = Math.ceil(span / 4) + 1;
    for (let k = 0; k < layers; k++) {
      const y = top + 1 + k;
      if (alongX) {
        const za = z0 - 1 + 2 * k;
        const zb = z1 + 1 - 2 * k;
        if (za >= zb) break;
        // Sloped surfaces (2 voxels deep) and filled gable ends.
        this.box(x0 - 1, y, za, x1 + 1, y + 1, Math.min(za + 3, zb), roofB);
        this.box(x0 - 1, y, Math.max(zb - 3, za), x1 + 1, y + 1, zb, roofB);
        this.box(x0, y, za + 3, x0 + t, y + 1, zb - 3, s.wall);
        this.box(x1 - t, y, za + 3, x1, y + 1, zb - 3, s.wall);
      } else {
        const xa = x0 - 1 + 2 * k;
        const xb = x1 + 1 - 2 * k;
        if (xa >= xb) break;
        this.box(xa, y, z0 - 1, Math.min(xa + 3, xb), y + 1, z1 + 1, roofB);
        this.box(Math.max(xb - 3, xa), y, z0 - 1, xb, y + 1, z1 + 1, roofB);
        this.box(xa + 3, y, z0, xb - 3, y + 1, z0 + t, s.wall);
        this.box(xa + 3, y, z1 - t, xb - 3, y + 1, z1, s.wall);
      }
    }
    return { top: top + layers, flights };
  }

  /** The floor just inside a door that must stay free of stairs. */
  private doorZone(d: DoorSpec, start: number, s: BuildingSpec, t: number): Rect {
    const w = d.w ?? 10;
    const ix0 = s.x0 + t;
    const ix1 = s.x1 - t;
    const iz0 = s.z0 + t;
    const iz1 = s.z1 - t;
    if (d.side === 'n') return { x0: start, z0: iz0, x1: start + w, z1: iz0 + DOOR_ZONE };
    if (d.side === 's') return { x0: start, z0: iz1 - DOOR_ZONE, x1: start + w, z1: iz1 };
    if (d.side === 'w') return { x0: ix0, z0: start, x1: ix0 + DOOR_ZONE, z1: start + w };
    return { x0: ix1 - DOOR_ZONE, z0: start, x1: ix1, z1: start + w };
  }

  /**
   * Moves a door that would open onto the stairs along its wall to the nearest clear spot; a door that cannot be moved (the wall is too short) is dropped,
   * as long as the building keeps at least one other door. Returns the doors to cut.
   */
  private slideDoors(s: BuildingSpec, flights: Flight[], t: number): DoorSpec[] {
    const ground = flights.filter((fl) => fl.floor === 0 && !fl.roof);
    if (!ground.length || !s.doors?.length) return s.doors ?? [];
    const out: DoorSpec[] = [];
    for (const d of s.doors) {
      const w = d.w ?? 10;
      const ns = d.side === 'n' || d.side === 's';
      const along0 = ns ? s.x0 : s.z0;
      const along1 = ns ? s.x1 : s.z1;
      const start = Math.round(this.doorCenter(d, s.x0, s.z0, s.x1, s.z1) - w / 2);
      const blockers = ground.flatMap((fl) => (fl.platform ? [fl.steps, fl.platform.rect] : [fl.steps]));
      const hit = blockers.filter((r) => rectsTouch(r, this.doorZone(d, start, s, t)));
      if (!hit.length) {
        out.push(d);
        continue;
      }
      const lo = Math.min(...hit.map((r) => (ns ? r.x0 : r.z0)));
      const hi = Math.max(...hit.map((r) => (ns ? r.x1 : r.z1)));
      const options = [lo - 2 - w, hi + 2]
        .filter((a) => a >= along0 + t + 3 && a + w <= along1 - t - 3)
        .filter((a) => !blockers.some((r) => rectsTouch(r, this.doorZone(d, a, s, t))));
      options.sort((a, b) => Math.abs(a - start) - Math.abs(b - start));
      if (options.length) out.push({ ...d, at: options[0] - along0 });
      else if (s.doors.length > 1) continue; // dropped: the stairs fill this wall
      else out.push(d);
    }
    // Never leave a building without a door.
    return out.length ? out : s.doors;
  }

  /**
   * Plans the flights of a building. For each floor it tries eight placements (along the north, south, west or east wall, climbing either way),
   * the usual one first, and takes the first that fits and keeps clear of the doors and of the earlier flights; if none is clean, the one with the
   * fewest conflicts. A flight needs a clear foot (STAIR_FOOT) in front of it, a landing at the top and room beside the foot to walk in; a
   * building too small for that gets no stairs.
   */
  private planStairs(s: BuildingSpec, t: number, floorH: number): Flight[] {
    const roof = !!s.roofAccess && s.roofStyle === 'flat';
    const count = s.floors - 1 + (roof ? 1 : 0);
    const ix0 = s.x0 + t;
    const ix1 = s.x1 - t;
    const iz0 = s.z0 + t;
    const iz1 = s.z1 - t;
    const W = STAIR_WIDTH;
    const doorRects: Rect[] = (s.doors ?? []).map((d) => this.doorZone(d, Math.round(this.doorCenter(d, s.x0, s.z0, s.x1, s.z1) - (d.w ?? 10) / 2), s, t));
    const out: Flight[] = [];
    for (let f = 0; f < count; f++) {
      const isRoof = f === s.floors - 1;
      // A flight climbs from this floor's surface to the next one's (the roof deck is two voxels above the top slab's underside).
      const height = isRoof ? floorH + 1 : floorH - 1;
      const steps = Math.ceil(height / STAIR_RISE);
      const run = steps * STAIR_TREAD;
      const y = s.y0 + f * floorH + 1;
      type Cand = { rec: StairRecord; steps: Rect; foot: Rect; exit: Rect; open: Rect; wall: Side };
      const cands: Cand[] = [];
      const need = STAIR_FOOT + run + STAIR_TOP;
      // The flight runs along x (along a north or south wall) or along z (west or east wall), climbing either way; it stands against the wall or, when that
      // is blocked by doors, one door zone in from it. `lo` / `hi` are the inner faces of the two end walls along the run.
      const place = (alongX: boolean, wall: Side, dir: Side, inset: number): void => {
        const lo = alongX ? ix0 : iz0;
        const hi = alongX ? ix1 : iz1;
        const sideLo = alongX ? iz0 : ix0;
        const sideHi = alongX ? iz1 : ix1;
        if (hi - lo < need || sideHi - sideLo < W + 14 + inset) return;
        const across = wall === 'n' || wall === 'w' ? sideLo + inset : sideHi - W - inset;
        const fwd = dir === 'e' || dir === 's';
        // First step cell and the extent of the steps along the run.
        const start = fwd ? lo + STAIR_FOOT : hi - STAIR_FOOT - 1;
        const s0 = fwd ? start : start - run + 1;
        const s1 = s0 + run;
        const rec: StairRecord = { x: alongX ? start : across, y, z: alongX ? across : start, dir, width: W, steps, rise: STAIR_RISE, tread: STAIR_TREAD, height };
        const R = (a0: number, a1: number, b0: number, b1: number): Rect => (alongX ? { x0: a0, z0: b0, x1: a1, z1: b1 } : { x0: b0, z0: a0, x1: b1, z1: a1 });
        const footR = fwd ? R(lo, s0, across, across + W) : R(s1, hi, across, across + W);
        const exitR = fwd ? R(s1, s1 + STAIR_TOP, across, across + W) : R(s0 - STAIR_TOP, s0, across, across + W);
        const openR = fwd ? R(s0 - STAIR_BEHIND, s1, across, across + W) : R(s0, s1 + STAIR_BEHIND, across, across + W);
        cands.push({ rec, wall, steps: R(s0, s1, across, across + W), foot: footR, exit: exitR, open: openR });
      };
      const insets = [0, DOOR_ZONE + 2];
      const alongWalls: Side[] = f % 2 === 0 ? ['n', 's'] : ['s', 'n'];
      for (const inset of insets) {
        for (const wall of alongWalls) for (const dir of ['e', 'w'] as const) place(true, wall, dir, inset);
        for (const wall of ['w', 'e'] as const) for (const dir of ['s', 'n'] as const) place(false, wall, dir, inset);
      }
      if (!cands.length) {
        const sb = this.planSwitchback(s, t, f, y, height, isRoof, doorRects, out);
        if (sb) {
          out.push(...sb);
          continue;
        }
      }
      if (!cands.length && STAIR_PLAN.lenient) {
        STAIR_PLAN.misfits.push({ x0: s.x0, z0: s.z0, x1: s.x1, z1: s.z1, floors: s.floors, floor: f, need, have: Math.max(ix1 - ix0, iz1 - iz0), across: Math.min(ix1 - ix0, iz1 - iz0) });
        continue;
      }
      if (!cands.length)
        throw new Error(
          `building ${s.x1 - s.x0} x ${s.z1 - s.z0} voxels cannot hold the stairs from floor ${f} to ${f + 1}: a straight flight needs ${need} voxels (${STAIR_FOOT} foot + ${run} run + ${STAIR_TOP} landing) of clear inside length and ` +
            `${W + 14} across, a switchback ${STAIR_FOOT + Math.ceil(steps / 2) * STAIR_TREAD + STAIR_LAND} by ${2 * STAIR_LANE + 4}; make the building bigger or give it fewer floors, or pass stairs: false and build the flight by hand`,
        );
      // Only the flight on the floor below cuts a hole in the floor this one starts on.
      const prev = out.filter((p) => !p.roof && p.floor === f - 1);
      let best = cands[0];
      let bestV = Infinity;
      for (const c of cands) {
        const flush = c.wall === 'n' ? c.steps.z0 === iz0 : c.wall === 's' ? c.steps.z1 === iz1 : c.wall === 'w' ? c.steps.x0 === ix0 : c.steps.x1 === ix1;
        let v = flush ? 0 : 0.5;
        if (f === 0) for (const d of doorRects) if (rectsTouch(c.steps, d)) v += 3;
        // Ground-floor doors only matter for flight 0; upper flights start above the door heads.
        for (const p of prev) {
          if (rectsTouch(c.foot, p.open) || rectsTouch(c.steps, p.open)) v += 3;
          if (rectsTouch(c.open, p.exit)) v += 3;
        }
        if (v < bestV) {
          bestV = v;
          best = c;
          if (v === 0) break;
        }
      }
      out.push({ rec: best.rec, floor: f, roof: isRoof, steps: best.steps, foot: best.foot, exit: best.exit, open: best.open, wall: best.wall });
    }
    return out;
  }

  /**
   * Switchback stairs: lane A climbs from the foot to a landing at the far end, lane B (beside it) climbs back from the landing to the next floor, which
   * is reached over A's foot. Tries both ends and both walls (and both turning ends) and the first that fits and keeps clear of doors and earlier flights
   * is used. Returns null if the room is too small even for this.
   */
  private planSwitchback(s: BuildingSpec, t: number, f: number, y: number, height: number, isRoof: boolean, doorRects: Rect[], prev: Flight[]): Flight[] | null {
    const ix0 = s.x0 + t;
    const ix1 = s.x1 - t;
    const iz0 = s.z0 + t;
    const iz1 = s.z1 - t;
    const L = STAIR_LANE;
    const steps = Math.ceil(height / STAIR_RISE);
    // Lane A takes the larger half: lane B then ends beside A's steps, never beside (or in) A's foot.
    const nA = Math.ceil(steps / 2);
    const nB = steps - nA;
    // The first step is the short one if the climb is not a multiple of the rise: A's climb is its steps minus that shortfall.
    const hA = nA * STAIR_RISE - (steps * STAIR_RISE - height);
    const hB = height - hA;
    const runA = nA * STAIR_TREAD;
    const runB = nB * STAIR_TREAD;
    const need = STAIR_FOOT + runA + STAIR_LAND;
    type Opt = { flights: Flight[]; v: number };
    let best: Opt | null = null;
    // alongX: the lanes run along x (the room is at least `need` long in x) with the two lanes stacked in z, else along z.
    for (const alongX of [true, false]) {
      const lo = alongX ? ix0 : iz0;
      const hi = alongX ? ix1 : iz1;
      const sLo = alongX ? iz0 : ix0;
      const sHi = alongX ? iz1 : ix1;
      if (hi - lo < need || sHi - sLo < 2 * L + 4) continue;
      for (const fwd of [true, false]) {
        for (const lane0 of [true, false]) {
          // lane A at the wall side (or the inner side); lane B next to it.
          const aAcross = lane0 ? sLo : sHi - 2 * L;
          const bAcross = aAcross + L;
          const R = (a0: number, a1: number, b0: number, b1: number): Rect => (alongX ? { x0: a0, z0: b0, x1: a1, z1: b1 } : { x0: b0, z0: a0, x1: b1, z1: a1 });
          const startA = fwd ? lo + STAIR_FOOT : hi - STAIR_FOOT - 1;
          const a0 = fwd ? startA : startA - runA + 1;
          const a1 = a0 + runA;
          const endA = fwd ? a1 : a0; // the end of lane A where the landing is
          const landR = fwd ? R(a1, a1 + STAIR_LAND, aAcross, aAcross + 2 * L) : R(a0 - STAIR_LAND, a0, aAcross, aAcross + 2 * L);
          // Lane B climbs back toward the foot end, beside A; its first step is next to the landing.
          const bStart = fwd ? endA - 1 : endA;
          const b0 = fwd ? endA - runB : endA;
          const b1 = b0 + runB;
          const dirA: Side = alongX ? (fwd ? 'e' : 'w') : fwd ? 's' : 'n';
          const dirB: Side = alongX ? (fwd ? 'w' : 'e') : fwd ? 'n' : 's';
          const recA: StairRecord = { x: alongX ? startA : aAcross, y, z: alongX ? aAcross : startA, dir: dirA, width: L, steps: nA, rise: STAIR_RISE, tread: STAIR_TREAD, height: hA };
          const recB: StairRecord = { x: alongX ? (fwd ? bStart : b0) : bAcross, y: y + hA, z: alongX ? bAcross : fwd ? bStart : b0, dir: dirB, width: L, steps: nB, rise: STAIR_RISE, tread: STAIR_TREAD, height: hB, landing: true };
          // Opening in the slab above: over lane B and the landing, and over lane A where its head room is short; one rectangle over both lanes keeps it simple.
          const open = fwd ? R(b0 - STAIR_BEHIND, a1 + STAIR_LAND, aAcross, aAcross + 2 * L) : R(a0 - STAIR_LAND, b1 + STAIR_BEHIND, aAcross, aAcross + 2 * L);
          const foot = fwd ? R(lo, a0, aAcross, aAcross + L) : R(a1, hi, aAcross, aAcross + L);
          const exit = fwd ? R(b0 - STAIR_TOP, b0, bAcross, bAcross + L) : R(b1, b1 + STAIR_TOP, bAcross, bAcross + L);
          const wall: Side = alongX ? (lane0 ? 'n' : 's') : lane0 ? 'w' : 'e';
          const platform = { rect: landR, top: hA };
          const fl = (rec: StairRecord, stepsR: Rect, footR: Rect, exitR: Rect, plat?: { rect: Rect; top: number }): Flight => ({ rec, floor: f, roof: isRoof, steps: stepsR, foot: footR, exit: exitR, open, wall, platform: plat });
          const flA = fl(recA, R(a0, a1, aAcross, aAcross + L), foot, landR, platform);
          const flB = fl(recB, R(b0, b1, bAcross, bAcross + L), landR, exit);
          let v = 0;
          if (f === 0) for (const d of doorRects) if (rectsTouch(flA.steps, d) || rectsTouch(flB.steps, d) || rectsTouch(landR, d)) v += 3;
          for (const p of prev.filter((q) => !q.roof && q.floor === f - 1)) {
            if (rectsTouch(flA.foot, p.open) || rectsTouch(flA.steps, p.open) || rectsTouch(landR, p.open)) v += 3;
            if (rectsTouch(open, p.exit)) v += 3;
          }
          if (!lane0) v += 0.2;
          if (!best || v < best.v) best = { flights: [flA, flB], v };
        }
      }
    }
    return best ? best.flights : null;
  }

  private cutWindows(x0: number, z0: number, x1: number, z1: number, y: number, win: WindowSpec, t: number, doors: DoorSpec[], flights: Flight[] = []): void {
    const sides: [Side, number, number][] = [
      ['n', x0, x1],
      ['s', x0, x1],
      ['w', z0, z1],
      ['e', z0, z1],
    ];
    for (const [side, a0, a1] of sides) {
      const len = a1 - a0;
      const count = Math.floor((len - 6) / win.spacing);
      if (count <= 0) continue;
      const start = a0 + Math.floor((len - (count - 1) * win.spacing - win.w) / 2);
      for (let i = 0; i < count; i++) {
        const a = start + i * win.spacing;
        // Skip windows overlapping a door on this side.
        if (doors.some((d) => d.side === side && Math.abs(this.doorCenter(d, x0, z0, x1, z1) - (a + win.w / 2)) < (d.w ?? 10) / 2 + win.w / 2 + 2)) continue;
        // Skip windows a flight of stairs rises against.
        if (flights.some((fl) => fl.wall === side && a < (side === 'n' || side === 's' ? fl.steps.x1 : fl.steps.z1) + 2 && a + win.w > (side === 'n' || side === 's' ? fl.steps.x0 : fl.steps.z0) - 2)) continue;
        this.opening(side, x0, z0, x1, z1, a, a + win.w, y, y + win.h, t, win.glass, 'window');
      }
    }
  }

  private doorCenter(d: DoorSpec, x0: number, z0: number, x1: number, z1: number): number {
    const along = d.side === 'n' || d.side === 's' ? [x0, x1] : [z0, z1];
    const w = d.w ?? 10;
    return d.at !== undefined ? along[0] + d.at + w / 2 : (along[0] + along[1]) / 2;
  }

  private cutDoor(x0: number, z0: number, x1: number, z1: number, y: number, d: DoorSpec, t: number): void {
    const w = d.w ?? 10;
    const h = d.h ?? 22;
    const c = this.doorCenter(d, x0, z0, x1, z1);
    const a = Math.round(c - w / 2);
    this.opening(d.side, x0, z0, x1, z1, a, a + w, y, y + h, t, null, 'door');
  }

  /** Cuts a rectangular hole through one wall; optionally fills its middle layer with glass. */
  opening(side: Side, x0: number, z0: number, x1: number, z1: number, a0: number, a1: number, y0: number, y1: number, t: number, glass: number | null, kind: 'door' | 'window' = 'window'): void {
    if (side === 'n' || side === 's') {
      const za = side === 'n' ? z0 : z1 - t;
      if (OPENING_AUDIT.on) OPENING_AUDIT.list.push({ kind, side, x0: a0, y0, z0: za, x1: a1, y1, z1: za + t });
      this.clear(a0, y0, za, a1, y1, za + t);
      if (glass !== null) this.box(a0, y0, za + (t >> 1), a1, y1, za + (t >> 1) + 1, glass);
    } else {
      const xa = side === 'w' ? x0 : x1 - t;
      if (OPENING_AUDIT.on) OPENING_AUDIT.list.push({ kind, side, x0: xa, y0, z0: a0, x1: xa + t, y1, z1: a1 });
      this.clear(xa, y0, a0, xa + t, y1, a1);
      if (glass !== null) this.box(xa + (t >> 1), y0, a0, xa + (t >> 1) + 1, y1, a1, glass);
    }
  }

  // Props -------------------------------------------------------------------------------

  tree(x: number, y: number, z: number, height = 40, canopy = 14, leaves: Brush = speckle(family('leaves')), trunk: Brush = M('log_1')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, trunk);
    this.blob(x, y + height, z, canopy, canopy * 0.8, canopy, leaves, 0.35);
    this.blob(x + (canopy >> 1), y + height - (canopy >> 2), z, canopy * 0.7, canopy * 0.6, canopy * 0.7, leaves, 0.35);
    this.blob(x - (canopy >> 1), y + height - (canopy >> 2), z + (canopy >> 2), canopy * 0.6, canopy * 0.5, canopy * 0.6, leaves, 0.35);
  }

  pine(x: number, y: number, z: number, height = 60, leaves: Brush = speckle([M('leaves_3'), M('leaves_1')]), snow = false): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, M('log_2'));
    for (let k = 0; k < 5; k++) {
      const ly = y + 14 + k * ((height - 14) / 5);
      const r = 12 - k * 2;
      for (let dy = 0; dy < 6; dy++) {
        this.cylinder(x, z, Math.floor(ly + dy), Math.floor(ly + dy) + 1, r - dy * 0.8, leaves);
      }
      if (snow) this.cylinder(x, z, Math.floor(ly + 6), Math.floor(ly + 7), r - 5, M('snow_1'));
    }
  }

  crate(x: number, y: number, z: number, size = 10): void {
    const edge = M('planks_dark_1');
    this.box(x, y, z, x + size, y + size, z + size, planks(family('planks')));
    // Dark frame on the edges.
    for (const [a, b] of [
      [0, 0],
      [size - 1, 0],
      [0, size - 1],
      [size - 1, size - 1],
    ]) {
      this.box(x + a, y, z + b, x + a + 1, y + size, z + b + 1, edge);
      this.box(x, y + a, z + b, x + size, y + a + 1, z + b + 1, edge);
      this.box(x + a, y + b, z, x + a + 1, y + b + 1, z + size, edge);
    }
  }

  barrel(x: number, y: number, z: number, color = M('paint_red')): void {
    this.cylinder(x, z, y, y + 9, 3.2, color);
    this.cylinder(x, z, y + 2, y + 3, 3.4, M('steel_dark_1'));
    this.cylinder(x, z, y + 6, y + 7, 3.4, M('steel_dark_1'));
  }

  lamp(x: number, y: number, z: number, height = 32, glow = M('neon_yellow')): void {
    this.box(x, y, z, x + 1, y + height, z + 1, M('steel_dark_1'));
    this.box(x - 1, y + height, z - 1, x + 2, y + height + 2, z + 2, glow);
    this.box(x - 1, y + height + 2, z - 1, x + 2, y + height + 3, z + 2, M('steel_dark_2'));
  }

  bench(x: number, y: number, z: number, alongX = true): void {
    const legs = M('steel_dark_1');
    const seat = planks(family('planks'));
    if (alongX) {
      this.box(x, y + 3, z, x + 14, y + 4, z + 4, seat);
      this.box(x, y + 4, z + 3, x + 14, y + 8, z + 4, seat);
      this.box(x + 1, y, z + 1, x + 2, y + 3, z + 3, legs);
      this.box(x + 12, y, z + 1, x + 13, y + 3, z + 3, legs);
    } else {
      this.box(x, y + 3, z, x + 4, y + 4, z + 14, seat);
      this.box(x + 3, y + 4, z, x + 4, y + 8, z + 14, seat);
      this.box(x + 1, y, z + 1, x + 3, y + 3, z + 2, legs);
      this.box(x + 1, y, z + 12, x + 3, y + 3, z + 13, legs);
    }
  }

  car(x: number, y: number, z: number, color: number, alongX = true): void {
    const [L, W] = [40, 18];
    const glass = M('glass_tint');
    const tire = M('rubber');
    const bx = alongX ? L : W;
    const bz = alongX ? W : L;
    this.box(x, y + 3, z, x + bx, y + 11, z + bz, color);
    // Cabin.
    if (alongX) {
      this.box(x + 10, y + 11, z + 1, x + 30, y + 18, z + W - 1, glass);
      this.box(x + 11, y + 18, z + 1, x + 29, y + 19, z + W - 1, color);
      for (const wx of [x + 5, x + L - 11]) for (const wz of [z - 1, z + W - 4]) this.box(wx, y, wz, wx + 7, y + 7, wz + 5, tire);
      this.box(x + L - 1, y + 7, z + 2, x + L, y + 9, z + 6, M('neon_yellow'));
      this.box(x + L - 1, y + 7, z + W - 6, x + L, y + 9, z + W - 2, M('neon_yellow'));
    } else {
      this.box(x + 1, y + 11, z + 10, x + W - 1, y + 18, z + 30, glass);
      this.box(x + 1, y + 18, z + 11, x + W - 1, y + 19, z + 29, color);
      for (const wz of [z + 5, z + L - 11]) for (const wx of [x - 1, x + W - 4]) this.box(wx, y, wz, wx + 5, y + 7, wz + 7, tire);
    }
  }

  fence(x0: number, z0: number, x1: number, z1: number, y: number, b: Brush = planks(family('planks'))): void {
    const alongX = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
    if (alongX) {
      for (let x = Math.min(x0, x1); x < Math.max(x0, x1); x++) {
        if (x % 8 === 0) this.box(x, y, z0, x + 1, y + 11, z0 + 1, b);
        this.box(x, y + 4, z0, x + 1, y + 5, z0 + 1, b);
        this.box(x, y + 8, z0, x + 1, y + 9, z0 + 1, b);
      }
    } else {
      for (let z = Math.min(z0, z1); z < Math.max(z0, z1); z++) {
        if (z % 8 === 0) this.box(x0, y, z, x0 + 1, y + 11, z + 1, b);
        this.box(x0, y + 4, z, x0 + 1, y + 5, z + 1, b);
        this.box(x0, y + 8, z, x0 + 1, y + 9, z + 1, b);
      }
    }
  }

  /** Market stall with a striped cloth roof. */
  stall(x: number, y: number, z: number, cloth: number): void {
    const post = M('log_1');
    for (const [a, b] of [
      [0, 0],
      [19, 0],
      [0, 13],
      [19, 13],
    ])
      this.box(x + a, y, z + b, x + a + 1, y + 22, z + b + 1, post);
    this.box(x, y + 8, z, x + 20, y + 9, z + 14, planks(family('planks')));
    this.box(x - 1, y + 22, z - 1, x + 21, y + 23, z + 15, stripes(cloth, M('cloth_white'), 3, 'x'));
    this.box(x + 2, y + 9, z + 2, x + 6, y + 12, z + 6, M('pumpkin'));
    this.box(x + 8, y + 9, z + 3, x + 12, y + 11, z + 7, M('paint_lime'));
  }

  fountain(cx: number, y: number, cz: number, r = 22): void {
    const stone = blocks(family('marble'), 3);
    this.cylinder(cx, cz, y, y + 6, r, stone);
    this.cylinder(cx, cz, y + 1, y + 6, r - 3, AIR);
    this.cylinder(cx, cz, y, y + 4, r - 3, M('water'));
    this.cylinder(cx, cz, y, y + 26, 3, stone);
    this.cylinder(cx, cz, y + 26, y + 28, 8, stone);
    this.cylinder(cx, cz, y + 28, y + 30, 5, M('water'));
  }

  /** Blocky statue of a Voxel Marine on a plinth (one world voxel per character voxel). */
  statue(cx: number, y: number, cz: number, b: Brush = M('gold')): void {
    this.box(cx - 8, y, cz - 8, cx + 8, y + 8, cz + 8, blocks(family('marble'), 4));
    const base = y + 8;
    this.box(cx - 4, base, cz - 2, cx, base + 12, cz + 2, b);
    this.box(cx, base, cz - 2, cx + 4, base + 12, cz + 2, b);
    this.box(cx - 4, base + 12, cz - 2, cx + 4, base + 24, cz + 2, b);
    this.box(cx - 8, base + 12, cz - 2, cx - 4, base + 24, cz + 2, b);
    this.box(cx + 4, base + 18, cz - 2, cx + 8, base + 32, cz + 2, b);
    this.box(cx - 4, base + 24, cz - 4, cx + 4, base + 32, cz + 4, b);
  }

  // Whole-map operations -----------------------------------------------------------------

  /**
   * Completes a symmetric map by copying its low half onto the other half (chunk by chunk, so it
   * is fast), exchanging team materials. Build one team's half first, symmetrize, then add
   * anything that straddles the center line.
   */
  symmetrize(kind: Symmetry, swaps: [number, number][] = teamSwaps()): void {
    const w = this.w;
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) lut[i] = i;
    for (const [a, b] of swaps) {
      lut[a] = b;
      lut[b] = a;
    }
    const swapVals: number[] = [];
    for (const [a, b] of swaps) swapVals.push(a, b);
    const vol = CHUNK_VOLUME;
    const alongX = kind === 'mirrorX' || kind === 'rotateX';
    const flipX = kind !== 'mirrorZ';
    const flipZ = kind !== 'mirrorX';
    if ((alongX ? w.ncx : w.ncz) % 2 !== 0) throw new Error('symmetrize needs an even chunk count along the split axis');
    const hx = alongX ? w.ncx >> 1 : w.ncx;
    const hz = alongX ? w.ncz : w.ncz >> 1;
    if (OPENING_AUDIT.on) {
      // Doors and windows of the copied half, for the clearance audit.
      const flip: Record<Side, Side> = { e: 'w', w: 'e', n: 's', s: 'n' };
      const copies: OpeningRecord[] = [];
      for (const r of OPENING_AUDIT.list) {
        if (alongX ? r.x1 > w.sx / 2 : r.z1 > w.sz / 2) continue;
        copies.push({
          kind: r.kind,
          side: (flipX && (r.side === 'e' || r.side === 'w')) || (flipZ && (r.side === 'n' || r.side === 's')) ? flip[r.side] : r.side,
          x0: flipX ? w.sx - r.x1 : r.x0,
          x1: flipX ? w.sx - r.x0 : r.x1,
          y0: r.y0,
          y1: r.y1,
          z0: flipZ ? w.sz - r.z1 : r.z0,
          z1: flipZ ? w.sz - r.z0 : r.z1,
        });
      }
      OPENING_AUDIT.list.push(...copies);
    }
    if (STAIR_AUDIT.on) {
      // The copied half's staircases get audited too.
      const copies: StairRecord[] = [];
      for (const r of STAIR_AUDIT.list) {
        const m = mirrorStair(r, flipX, flipZ, alongX, w.sx, w.sz);
        if (m) copies.push(m);
      }
      STAIR_AUDIT.list.push(...copies);
    }
    for (let cy = 0; cy < w.ncy; cy++)
      for (let cz = 0; cz < hz; cz++)
        for (let cx = 0; cx < hx; cx++) {
          const src = w.chunks[w.chunkIndex(cx, cy, cz)];
          const dst = w.chunks[w.chunkIndex(flipX ? w.ncx - 1 - cx : cx, cy, flipZ ? w.ncz - 1 - cz : cz)];
          dst.dmg = null;
          dst.rev++;
          const s = src.data;
          if (s === null) {
            dst.data = null;
            dst.fill = lut[src.fill];
            continue;
          }
          const d = dst.data ?? new Uint8Array(vol);
          // Copy 32-voxel rows as eight 32-bit words (byte-swapped and reversed when flipping x).
          const s32 = new Uint32Array(s.buffer, s.byteOffset, vol >> 2);
          const d32 = new Uint32Array(d.buffer, d.byteOffset, vol >> 2);
          for (let ly = 0; ly < 32; ly++)
            for (let lz = 0; lz < 32; lz++) {
              const si = ((lz << 5) | (ly << 10)) >> 2;
              const di = (((flipZ ? 31 - lz : lz) << 5) | (ly << 10)) >> 2;
              if (flipX)
                for (let k = 0; k < 8; k++) {
                  const v = s32[si + k];
                  d32[di + 7 - k] = ((v & 0xff) << 24) | ((v & 0xff00) << 8) | ((v >>> 8) & 0xff00) | (v >>> 24);
                }
              else for (let k = 0; k < 8; k++) d32[di + k] = s32[si + k];
            }
          // Team materials are rare: only chunks that hold one get the per-voxel swap.
          let has = false;
          for (let k = 0; k < swapVals.length && !has; k++) if (s.indexOf(swapVals[k]) >= 0) has = true;
          if (has) for (let i = 0; i < vol; i++) d[i] = lut[d[i]];
          dst.data = d;
        }
  }

  /**
   * Fills the world from a heightmap with layered ground (bedrock, deep, sub, surface) and
   * optional water, writing chunk storage directly. Call it first: it replaces the chunks it
   * touches. Chunks that end up a single material stay compact.
   */
  heightfield(t: TerrainSpec): void {
    const w = this.w;
    const sx = w.sx;
    const H = t.height;
    const subDepth = t.subDepth ?? 4;
    const water = t.water ?? AIR;
    const lvl = t.water !== undefined ? (t.waterLevel ?? 0) : 0;
    const bed = t.bedrock ?? true;
    const surfArr = typeof t.surface === 'number' ? null : t.surface;
    const surf0 = typeof t.surface === 'number' ? t.surface : 0;
    const subArr = typeof t.sub === 'number' ? null : t.sub;
    const sub0 = typeof t.sub === 'number' ? t.sub : 0;
    const deep = t.deep;
    const bedTop = t.bedrockTop ?? 2;
    const bedMin = t.bedrockMin ?? 0;
    const subCol = t.subCol ?? null;
    // Imported bindings are getter calls under some module loaders: keep hot values local.
    const air = AIR;
    const bedrock = BEDROCK;
    const hash = hash32;
    for (let cz = 0; cz < w.ncz; cz++)
      for (let cx = 0; cx < w.ncx; cx++) {
        let minH = 1 << 30;
        let maxH = -(1 << 30);
        // One material for every solid layer of every column (e.g. a flat sand sea bed)?
        const row0 = (cx << 5) + (cz << 5) * sx;
        const m0 = surfArr === null ? surf0 : surfArr[row0];
        let single = subArr === null && (subCol === null ? sub0 : subCol[row0]) === m0 && deep === m0;
        for (let lz = 0; lz < 32; lz++) {
          const row = (cx << 5) + ((cz << 5) + lz) * sx;
          for (let lx = 0; lx < 32; lx++) {
            const h = H[row + lx];
            if (h < minH) minH = h;
            if (h > maxH) maxH = h;
            if (single && ((surfArr !== null && surfArr[row + lx] !== m0) || (subCol !== null && subCol[row + lx] !== m0))) single = false;
          }
        }
        const top = Math.min(w.sy, Math.max(maxH, lvl));
        for (let cy = 0; cy < w.ncy; cy++) {
          const y0 = cy << 5;
          if (y0 >= top) break;
          const c = w.chunks[w.chunkIndex(cx, cy, cz)];
          c.dmg = null;
          c.rev++;
          // Every column has bedrock / no column has bedrock.
          const allBed = bed && minH > bedMin;
          const noBed = !bed || maxH <= bedMin;
          if (allBed && y0 + 32 <= bedTop) {
            c.data = null;
            c.fill = bedrock;
            continue;
          }
          if (minH > 0 && (noBed || y0 >= bedTop) && y0 + 32 <= minH - 1 - subDepth) {
            c.data = null;
            c.fill = deep;
            continue;
          }
          if (single && minH > 0 && (noBed || y0 >= bedTop) && y0 + 32 <= minH) {
            c.data = null;
            c.fill = m0;
            continue;
          }
          if (maxH <= y0 && y0 + 32 <= lvl) {
            c.data = null;
            c.fill = water;
            continue;
          }
          // Mixed chunk: write each column as runs (bedrock, deep, sub, surface, water, air).
          const d = c.data ?? new Uint8Array(CHUNK_VOLUME);
          const y1 = y0 + 32;
          const run = (i: number, a: number, b: number, v: number): void => {
            const lo = a > y0 ? a : y0;
            const hi = b < y1 ? b : y1;
            for (let y = lo; y < hi; y++) d[i | ((y - y0) << 10)] = v;
          };
          for (let lz = 0; lz < 32; lz++) {
            const z = (cz << 5) + lz;
            const row = (cx << 5) + z * sx;
            for (let lx = 0; lx < 32; lx++) {
              const col = row + lx;
              const h = H[col];
              const i = lx | (lz << 5);
              let solidTop = 0;
              if (h > 0) {
                const bedEnd = bed && h > bedMin ? Math.min(bedTop, h) : 0;
                const subStart = Math.max(bedEnd, h - 1 - subDepth);
                run(i, 0, bedEnd, bedrock);
                run(i, bedEnd, subStart, deep);
                if (subStart < h - 1 && h - 1 >= y0 && subStart < y1) {
                  if (subCol !== null) run(i, subStart, h - 1, subCol[col]);
                  else if (subArr === null) run(i, subStart, h - 1, sub0);
                  else for (let y = Math.max(subStart, y0); y < Math.min(h - 1, y1); y++) d[i | ((y - y0) << 10)] = subArr[hash((cx << 5) + lx, y, z, 77) % subArr.length];
                }
                if (h - 1 >= bedEnd) run(i, h - 1, h, surfArr === null ? surf0 : surfArr[col]);
                solidTop = h;
              }
              run(i, solidTop, lvl, water);
              run(i, Math.max(solidTop, lvl), y1, air);
            }
          }
          c.data = d;
        }
      }
  }

  /** First air y above the highest solid voxel at or below `from` in column (x, z). */
  surfaceY(x: number, z: number, from = this.sy - 1): number {
    const w = this.w;
    for (let y = Math.min(from, this.sy - 1); y >= 0; y--) if (w.get(x, y, z) !== 0) return y + 1;
    return 0;
  }

  /** Walking height in meters at a point (meters), searching down from `fromM` meters. */
  groundAt(xm: number, zm: number, fromM = this.sy / 10): number {
    return this.surfaceY(Math.floor(xm * 10), Math.floor(zm * 10), Math.floor(fromM * 10)) / 10;
  }

  /** Height (meters) a player standing at (xm, zm) rests at: the highest surface under its footprint. */
  standAt(xm: number, zm: number, fromM = this.sy / 10): number {
    let top = 0;
    const x0 = Math.floor((xm - 0.3) * 10);
    const z0 = Math.floor((zm - 0.3) * 10);
    for (let z = z0; z <= z0 + 6; z++) for (let x = x0; x <= x0 + 6; x++) top = Math.max(top, this.surfaceY(x, z, Math.floor(fromM * 10)));
    return top / 10;
  }

  // Structural helpers ---------------------------------------------------------------------

  /** Thick 3D line: a cube of edge t at every step from (x0, y0, z0) to (x1, y1, z1). */
  line(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, b: Brush, t = 1): void {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), Math.abs(z1 - z0));
    for (let i = 0; i <= n; i++) {
      const x = n === 0 ? x0 : x0 + Math.round(((x1 - x0) * i) / n);
      const y = n === 0 ? y0 : y0 + Math.round(((y1 - y0) * i) / n);
      const z = n === 0 ? z0 : z0 + Math.round(((z1 - z0) * i) / n);
      this.box(x, y, z, x + t, y + t, z + t, b);
    }
  }

  /**
   * Open lattice girder filling a half-open box and running along `axis`: four corner chords of
   * thickness t, a ring every `brace` voxels and zig-zag diagonals on all four faces.
   */
  lattice(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, axis: 'x' | 'y' | 'z', b: Brush, t = 2, brace = 12): void {
    const lo = [x0, y0, z0];
    const hi = [x1, y1, z1];
    const ai = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
    const ui = (ai + 1) % 3;
    const vi = (ai + 2) % 3;
    const p = [0, 0, 0];
    const q = [0, 0, 0];
    const put = (a0: number, a1: number, u0: number, u1: number, v0: number, v1: number): void => {
      p[ai] = a0;
      q[ai] = a1;
      p[ui] = u0;
      q[ui] = u1;
      p[vi] = v0;
      q[vi] = v1;
      this.box(p[0], p[1], p[2], q[0], q[1], q[2], b);
    };
    const diag = (a0: number, u0: number, v0: number, a1: number, u1: number, v1: number): void => {
      p[ai] = a0;
      p[ui] = u0;
      p[vi] = v0;
      q[ai] = a1;
      q[ui] = u1;
      q[vi] = v1;
      this.line(p[0], p[1], p[2], q[0], q[1], q[2], b, 1);
    };
    const A0 = lo[ai];
    const A1 = hi[ai];
    const U0 = lo[ui];
    const U1 = hi[ui];
    const V0 = lo[vi];
    const V1 = hi[vi];
    for (const [u, v] of [
      [U0, V0],
      [U1 - t, V0],
      [U0, V1 - t],
      [U1 - t, V1 - t],
    ])
      put(A0, A1, u, u + t, v, v + t);
    const ring = (a: number): void => {
      put(a, a + 1, U0, U1, V0, V0 + 1);
      put(a, a + 1, U0, U1, V1 - 1, V1);
      put(a, a + 1, U0, U0 + 1, V0, V1);
      put(a, a + 1, U1 - 1, U1, V0, V1);
    };
    let flip = false;
    for (let a = A0; a < A1 - 1; a += brace) {
      const e = Math.min(A1 - 1, a + brace);
      ring(a);
      const [s0, s1] = flip ? [e, a] : [a, e];
      diag(s0, U0, V0, s1, U1 - 1, V0);
      diag(s0, U0, V1 - 1, s1, U1 - 1, V1 - 1);
      diag(s0, U0, V0, s1, U0, V1 - 1);
      diag(s0, U1 - 1, V0, s1, U1 - 1, V1 - 1);
      flip = !flip;
    }
    ring(A1 - 1);
  }

  /**
   * Solid staircase of `steps` one-voxel steps climbing toward `dir` (e = +x, w = -x, s = +z,
   * n = -z). (x, z) is the first step's cell; the stairs extend `width` voxels along +z (e/w) or +x
   * (n/s). `thick` > 0 builds floating steps of that thickness instead of solid ones.
   */
  /**
   * Converts a flight that was laid out as 45 degree steps (`height` steps, one deep each, first step cell at (x, z)) to gentle stairs that END in the same
   * place: the first step moves back by the extra length. `open` = [y0, y1] cuts the hole through the slab above first (over the whole run and 0.3 m behind
   * the first step, across the width), so the climb has head room; it must be cut before the steps are built because the top steps reach into the slab.
   */
  climbEnding(x: number, y: number, z: number, dir: Side, width: number, height: number, b: Brush, opt: { thick?: number; open?: [number, number] } = {}): StairRecord {
    const steps = Math.ceil(height / STAIR_RISE);
    const run = steps * STAIR_TREAD;
    const delta = run - height;
    const fwd = dir === 'e' || dir === 's';
    const along = (dir === 'e' || dir === 'w' ? x : z) + (fwd ? -delta : delta);
    const sx = dir === 'e' || dir === 'w' ? along : x;
    const sz = dir === 'e' || dir === 'w' ? z : along;
    if (opt.open) {
      const a0 = fwd ? along - 3 : along - run + 1;
      const a1 = fwd ? along + run : along + 4;
      if (dir === 'e' || dir === 'w') this.clear(a0, opt.open[0], sz, a1, opt.open[1], sz + width);
      else this.clear(sx, opt.open[0], a0, sx + width, opt.open[1], a1);
    }
    return this.flight(sx, y, sz, dir, width, height, b, STAIR_RISE, STAIR_TREAD, opt.thick ?? 0);
  }

  /** Clears everything above the steps of a flight, up to y1 (a cutting through rising ground, so the steps have head room). Call before or after building the flight. */
  trenchAbove(r: StairRecord, y1: number, extra = 0): void {
    const t = stairTread(r);
    for (let i = 0; i < r.steps; i++) {
      const top = stairStepTop(r, i);
      if (r.dir === 'e') this.clear(r.x + i * t, top, r.z - extra, r.x + (i + 1) * t, y1, r.z + r.width + extra);
      else if (r.dir === 'w') this.clear(r.x - (i + 1) * t + 1, top, r.z - extra, r.x - i * t + 1, y1, r.z + r.width + extra);
      else if (r.dir === 's') this.clear(r.x - extra, top, r.z + i * t, r.x + r.width + extra, y1, r.z + (i + 1) * t);
      else this.clear(r.x - extra, top, r.z - (i + 1) * t + 1, r.x + r.width + extra, y1, r.z - i * t + 1);
    }
  }

  /** Rails 0.2 m thick along the open sides of a flight ('lo' = the lower x / z side, 'hi' = the other), rising 0.2 m above each step. */
  flightRails(r: StairRecord, brush: Brush, sides: ('lo' | 'hi')[] = ['lo', 'hi'], thick = 2): void {
    const t = stairTread(r);
    const ew = r.dir === 'e' || r.dir === 'w';
    for (let i = 0; i < r.steps; i++) {
      const top = stairStepTop(r, i) + 2;
      const a0 = r.dir === 'e' ? r.x + i * t : r.dir === 'w' ? r.x - (i + 1) * t + 1 : r.dir === 's' ? r.z + i * t : r.z - (i + 1) * t + 1;
      const a1 = a0 + t;
      for (const side of sides) {
        const c0 = ew ? (side === 'lo' ? r.z - thick : r.z + r.width) : side === 'lo' ? r.x - thick : r.x + r.width;
        if (ew) this.box(a0, r.y, c0, a1, top, c0 + thick, brush);
        else this.box(c0, r.y, a0, c0 + thick, top, a1, brush);
      }
    }
  }

  /** Plan rectangle (voxels) covered by a flight's steps. */
  flightRect(r: StairRecord): Rect {
    const run = stairRun(r);
    if (r.dir === 'e') return { x0: r.x, z0: r.z, x1: r.x + run, z1: r.z + r.width };
    if (r.dir === 'w') return { x0: r.x - run + 1, z0: r.z, x1: r.x + 1, z1: r.z + r.width };
    if (r.dir === 's') return { x0: r.x, z0: r.z, x1: r.x + r.width, z1: r.z + run };
    return { x0: r.x, z0: r.z - run + 1, x1: r.x + r.width, z1: r.z + 1 };
  }

  /** Clears the box from y0 up to y1 over the bounding rectangle of the given flights and rectangles, grown by `margin` voxels (a hole through a roof or floor above a stairwell). */
  openAbove(y0: number, y1: number, parts: (StairRecord | Rect)[], margin = 3): void {
    const rs = parts.map((p) => ('dir' in p ? this.flightRect(p) : p));
    this.clear(Math.min(...rs.map((r) => r.x0)) - margin, y0, Math.min(...rs.map((r) => r.z0)) - margin, Math.max(...rs.map((r) => r.x1)) + margin, y1, Math.max(...rs.map((r) => r.z1)) + margin);
  }

  /**
   * A U-shaped flight for a room too short for a straight one. Lane A (`lane` wide) climbs from the foot at (x, z) toward `dir` to a landing; lane B,
   * beside it on the `bSide` side (toward +z / +x when 'a', else -z / -x), climbs back to the floor above. Needs STAIR_FOOT + the run of A + STAIR_LAND clear along
   * `dir` and 2 * lane across. The solid under lane B and the landing is built here too. Returns both records (B marked `landing`: it has no foot of its own).
   */
  switchbackFlight(x: number, y: number, z: number, dir: Side, lane: number, height: number, b: Brush, bSide: 'a' | 'b' = 'a', thick = 0): [StairRecord, StairRecord, Rect] {
    const steps = Math.ceil(height / STAIR_RISE);
    const nA = Math.ceil(steps / 2);
    const nB = steps - nA;
    const hA = nA * STAIR_RISE - (steps * STAIR_RISE - height);
    const hB = height - hA;
    const ew = dir === 'e' || dir === 'w';
    const fwd = dir === 'e' || dir === 's';
    const runA = nA * STAIR_TREAD;
    const runB = nB * STAIR_TREAD;
    const sgn = bSide === 'a' ? 1 : -1;
    const A = this.flight(x, y, z, dir, lane, hA, b, STAIR_RISE, STAIR_TREAD, thick);
    // The landing: STAIR_LAND deep beyond A's last step, across both lanes, at A's top height.
    const endA = ew ? (fwd ? x + runA : x - runA + 1) : fwd ? z + runA : z - runA + 1; // first cell beyond A along dir
    const across0 = ew ? (sgn > 0 ? z : z - lane) : sgn > 0 ? x : x - lane;
    const lo = fwd ? endA : endA - STAIR_LAND;
    const hi = lo + STAIR_LAND;
    // `thick` > 0 means open steel stairs: a thin landing slab and nothing built under lane B.
    const landY0 = thick > 0 ? y + hA - thick : y;
    if (ew) this.box(lo, landY0, across0, hi, y + hA, across0 + 2 * lane, b);
    else this.box(across0, landY0, lo, across0 + 2 * lane, y + hA, hi, b);
    // Lane B starts beside A's last step, climbs back along the opposite direction.
    const dirB: Side = dir === 'e' ? 'w' : dir === 'w' ? 'e' : dir === 's' ? 'n' : 's';
    const bStartAlong = fwd ? endA - 1 : endA;
    const bx = ew ? bStartAlong : x + sgn * lane;
    const bz = ew ? z + sgn * lane : bStartAlong;
    const B2 = this.flight(bx, y + hA, bz, dirB, lane, hB, b, STAIR_RISE, STAIR_TREAD, thick);
    B2.landing = true;
    // Fill under lane B down to the floor so it is solid, not floating.
    const bLo = fwd ? endA - runB : endA;
    const bHi = bLo + runB;
    if (thick === 0) {
      if (ew) this.box(bLo, y, bz, bHi, y + hA, bz + lane, b);
      else this.box(bx, y, bLo, bx + lane, y + hA, bHi, b);
    }
    const landing: Rect = ew ? { x0: lo, z0: across0, x1: hi, z1: across0 + 2 * lane } : { x0: across0, z0: lo, x1: across0 + 2 * lane, z1: hi };
    return [A, B2, landing];
  }

  /** A gentle flight (0.2 m rise, 0.3 m tread, 34 degrees) climbing `height` voxels toward `dir`; the replacement for `stairs()` on hand-built maps. */
  climb(x: number, y: number, z: number, dir: Side, width: number, height: number, b: Brush, thick = 0): StairRecord {
    return this.flight(x, y, z, dir, width, height, b, STAIR_RISE, STAIR_TREAD, thick);
  }

  /**
   * A flight of real stairs: `height` voxels of climb in steps `rise` high and `tread` deep (default 0.2 m by 0.3 m, a 34 degree slope), climbing toward
   * `dir` from the first step's cell (x, z) at base y. The last step is always a full rise below the floor above, the first may be shorter, so the
   * top is flush whatever the height. Returns the record (also kept for the stairs audit).
   */
  flight(x: number, y: number, z: number, dir: Side, width: number, height: number, b: Brush, rise = STAIR_RISE, tread = STAIR_TREAD, thick = 0): StairRecord {
    const steps = Math.ceil(height / rise);
    const rec: StairRecord = { x, y, z, dir, width, steps, rise, tread, height };
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push(rec);
    for (let i = 0; i < steps; i++) {
      const top = stairStepTop(rec, i);
      const bot = thick > 0 ? Math.max(y, top - thick) : y;
      if (dir === 'e') this.box(x + i * tread, bot, z, x + (i + 1) * tread, top, z + width, b);
      else if (dir === 'w') this.box(x - (i + 1) * tread + 1, bot, z, x - i * tread + 1, top, z + width, b);
      else if (dir === 's') this.box(x, bot, z + i * tread, x + width, top, z + (i + 1) * tread, b);
      else this.box(x, bot, z - (i + 1) * tread + 1, x + width, top, z - i * tread + 1, b);
    }
    return rec;
  }

  stairs(x: number, y: number, z: number, dir: Side, width: number, steps: number, b: Brush, thick = 0): void {
    if (STAIR_AUDIT.on && steps > 0) STAIR_AUDIT.list.push({ x, y, z, dir, width, steps });
    for (let i = 0; i < steps; i++) {
      const top = y + i + 1;
      const bot = thick > 0 ? Math.max(y, top - thick) : y;
      if (dir === 'e') this.box(x + i, bot, z, x + i + 1, top, z + width, b);
      else if (dir === 'w') this.box(x - i, bot, z, x - i + 1, top, z + width, b);
      else if (dir === 's') this.box(x, bot, z + i, x + width, top, z + i + 1, b);
      else this.box(x, bot, z - i, x + width, top, z - i + 1, b);
    }
  }

  /** Battlements: merlons along the outer edge of a rectangular wall top at height y. */
  crenellate(x0: number, z0: number, x1: number, z1: number, y: number, h: number, t: number, b: Brush, merlon = 5, gap = 4): void {
    const period = merlon + gap;
    for (let x = x0; x < x1; x += period) {
      const xe = Math.min(x1, x + merlon);
      this.box(x, y, z0, xe, y + h, z0 + t, b);
      this.box(x, y, z1 - t, xe, y + h, z1, b);
    }
    for (let z = z0; z < z1; z += period) {
      const ze = Math.min(z1, z + merlon);
      this.box(x0, y, z, x0 + t, y + h, ze, b);
      this.box(x1 - t, y, z, x1, y + h, ze, b);
    }
  }

  /** Stepped cone (sand and gravel piles, roofs): layers of `step` voxels shrinking to a point. */
  cone(cx: number, y: number, cz: number, r: number, h: number, b: Brush, step = 2): void {
    for (let dy = 0; dy < h; dy += step) {
      const rr = r * (1 - dy / h);
      if (rr < 0.8) break;
      this.cylinder(cx, cz, y + dy, Math.min(y + h, y + dy + step), rr, b);
    }
  }

  /** Upper half of a sphere (domes, igloos); `inner` > 0 hollows it out. */
  dome(cx: number, y: number, cz: number, r: number, b: Brush, inner = 0): void {
    const R = Math.ceil(r);
    const r2 = r * r;
    const i2 = inner * inner;
    for (let dy = 0; dy <= R; dy++)
      for (let dz = -R; dz <= R; dz++)
        for (let dx = -R; dx <= R; dx++) {
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 > r2) continue;
          if (inner > 0 && d2 < i2) continue;
          this.set(cx + dx, y + dy, cz + dz, b);
        }
  }

  /**
   * Pixel-art panel on a vertical plane (neon signs, murals). Rows run top to bottom, one char per
   * pixel looked up in `colors` (other chars are skipped). (x, y, z) is the top-left pixel as seen
   * by someone facing the panel from side `face`; each pixel is px x px voxels and `depth` thick.
   */
  glyph(x: number, y: number, z: number, face: Side, rows: string[], colors: Record<string, number>, px = 1, depth = 1): void {
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      const ya = y - (r + 1) * px + 1;
      for (let c = 0; c < row.length; c++) {
        const v = colors[row[c]];
        if (v === undefined) continue;
        if (face === 's') this.box(x + c * px, ya, z, x + (c + 1) * px, ya + px, z + depth, v);
        else if (face === 'n') this.box(x - (c + 1) * px + 1, ya, z, x - c * px + 1, ya + px, z + depth, v);
        else if (face === 'w') this.box(x, ya, z + c * px, x + depth, ya + px, z + (c + 1) * px, v);
        else this.box(x, ya, z - (c + 1) * px + 1, x + depth, ya + px, z - c * px + 1, v);
      }
    }
  }

  // More props -----------------------------------------------------------------------------

  /** Broadleaf tree with a uniform canopy (merges into large faces, unlike speckled leaves). */
  leafyTree(x: number, y: number, z: number, height = 40, canopy = 13, leaves = M('leaves_2'), trunk = M('log_1')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, trunk);
    const c = canopy;
    this.blob(x, y + height, z, c, c * 0.72, c, leaves, 0);
    this.blob(x + (c >> 1), y + height - (c >> 2), z - (c >> 2), c * 0.7, c * 0.55, c * 0.7, leaves, 0);
    this.blob(x - (c >> 1), y + height + (c >> 3), z + (c >> 2), c * 0.65, c * 0.55, c * 0.65, leaves, 0);
    // A couple of branches reaching into the canopy.
    this.line(x, y + height - c, z, x + (c >> 1), y + height - (c >> 2), z - (c >> 2), trunk, 2);
    this.line(x, y + height - c + 3, z, x - (c >> 1), y + height - (c >> 2), z + (c >> 2), trunk, 2);
  }

  /** Octagonal slab (union of two boxes) centered on (cx, cz): cheap round-ish tiers and columns. */
  octagon(cx: number, cz: number, y0: number, y1: number, r: number, b: Brush): void {
    const c = Math.max(0, Math.round(r * 0.5));
    this.box(cx - r, y0, cz - c, cx + r + 1, y1, cz + c + 1, b);
    this.box(cx - c, y0, cz - r, cx + c + 1, y1, cz + r + 1, b);
    if (r >= 4) {
      const d = Math.round(r * 0.8);
      this.box(cx - d, y0, cz - d, cx + d + 1, y1, cz + d + 1, b);
    }
  }

  /** Conifer of stacked octagonal tiers (few large faces) with optional snow on each tier. */
  pineTree(x: number, y: number, z: number, height = 60, snow = false, needles = M('leaves_3')): void {
    this.box(x - 1, y, z - 1, x + 2, y + height, z + 2, M('log_2'));
    const tiers = 4;
    const base = y + Math.floor(height * 0.2);
    const span = y + height - base;
    const r0 = Math.max(6, Math.floor(height / 5));
    const th = Math.floor(span / tiers) + 4;
    for (let k = 0; k < tiers; k++) {
      const ty = base + Math.floor((k * span) / tiers);
      const rt = Math.max(3, Math.round(r0 * (1 - k / (tiers + 0.5))));
      const step = Math.max(1, Math.ceil(rt / 3));
      for (let s = 0; s < 3; s++) {
        const r = Math.max(1, rt - s * step);
        const sy = ty + Math.floor((s * th) / 3);
        const ey = ty + Math.floor(((s + 1) * th) / 3);
        this.octagon(x, z, sy, ey, r, needles);
        if (snow && s === 0) this.octagon(x, z, ey, ey + 1, r - 1, M('snow_1'));
      }
    }
    this.box(x, y + height, z, x + 1, y + height + 5, z + 1, needles);
    if (snow) this.box(x, y + height + 5, z, x + 1, y + height + 6, z + 1, M('snow_1'));
  }

  /** Palm tree: a trunk curving toward `dir` and a crown of drooping fronds. */
  palm(x: number, y: number, z: number, height = 50, dir: Side = 'e', leaves = M('leaves_4')): void {
    const [dx, dz] = dir === 'e' ? [1, 0] : dir === 'w' ? [-1, 0] : dir === 's' ? [0, 1] : [0, -1];
    const lean = Math.floor(height / 5);
    for (let i = 0; i < height; i++) {
      const off = Math.floor((i * i * lean) / (height * height));
      const tx = x + dx * off;
      const tz = z + dz * off;
      this.box(tx - 1, y + i, tz - 1, tx + 1, y + i + 1, tz + 1, (i >> 2) % 2 === 0 ? M('log_1') : M('log_2'));
    }
    const cx = x + dx * lean;
    const cz = z + dz * lean;
    const ty = y + height;
    this.box(cx - 2, ty - 1, cz - 2, cx + 2, ty + 2, cz + 2, leaves);
    const dirs: [number, number][] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
    for (const [fx, fz] of dirs) {
      const len = fx !== 0 && fz !== 0 ? 11 : 15;
      for (let s = 1; s <= len; s++) {
        const yy = ty + 1 - Math.floor((s * s) / 22);
        const px = cx + fx * s;
        const pz = cz + fz * s;
        const wdt = s < len - 3 ? 1 : 0;
        this.box(px - wdt, yy, pz - wdt, px + 1 + wdt, yy + 1, pz + 1 + wdt, leaves);
      }
    }
    for (const [ox, oz] of [[1, 1], [-2, 0], [0, -2]]) this.box(cx + ox, ty - 3, cz + oz, cx + ox + 2, ty - 1, cz + oz + 2, M('paint_brown'));
  }

  /** Low round shrub. */
  bush(x: number, y: number, z: number, r = 6, leaves = M('leaves_1')): void {
    this.blob(x, y + Math.floor(r * 0.4), z, r, r * 0.7, r, leaves, 0);
  }

  /** Boulder half-sunk into the ground. */
  rock(x: number, y: number, z: number, r = 8, b: Brush = M('stone_2')): void {
    this.blob(x, y + Math.floor(r / 4), z, r * 1.15, r * 0.75, r, b, 0, false);
    this.blob(x + (r >> 1), y + Math.floor(r / 2), z - (r >> 2), r * 0.6, r * 0.5, r * 0.6, b, 0, false);
  }

  /** Six-meter shipping container (60 x 26 x 24 voxels) with ribbed sides; hollow and open at one end. */
  container(x: number, y: number, z: number, alongX: boolean, color: number, open = true): void {
    const L = 60;
    const W = 24;
    const H = 26;
    const bx = alongX ? L : W;
    const bz = alongX ? W : L;
    const frame = M('steel_dark_1');
    // Body inset by one voxel on the long sides so the ribs stay inside the footprint.
    if (alongX) this.box(x, y, z + 1, x + bx, y + H, z + bz - 1, color);
    else this.box(x + 1, y, z, x + bx - 1, y + H, z + bz, color);
    // Ribs.
    for (let a = 3; a < L - 3; a += 4) {
      if (alongX) {
        this.box(x + a, y + 1, z, x + a + 2, y + H - 1, z + 1, color);
        this.box(x + a, y + 1, z + bz - 1, x + a + 2, y + H - 1, z + bz, color);
      } else {
        this.box(x, y + 1, z + a, x + 1, y + H - 1, z + a + 2, color);
        this.box(x + bx - 1, y + 1, z + a, x + bx, y + H - 1, z + a + 2, color);
      }
    }
    // Hollow interior with a plank floor.
    if (alongX) this.clear(x + 1, y + 1, z + 2, x + bx - 1, y + H - 1, z + bz - 2);
    else this.clear(x + 2, y + 1, z + 1, x + bx - 2, y + H - 1, z + bz - 1);
    // Corner posts.
    for (const [ax, az] of [
      [0, 0],
      [bx - 2, 0],
      [0, bz - 2],
      [bx - 2, bz - 2],
    ])
      this.box(x + ax, y, z + az, x + ax + 2, y + H, z + az + 2, frame);
    // Doors at the far end: open (walk-in) or closed with lock bars.
    if (alongX) {
      if (open) this.clear(x + bx - 2, y + 1, z + 2, x + bx, y + H - 2, z + bz - 2);
      else for (const zz of [z + 6, z + 10, z + 14, z + 18]) this.box(x + bx, y + 2, zz, x + bx + 1, y + H - 2, zz + 1, frame);
    } else if (open) this.clear(x + 2, y + 1, z + bz - 2, x + bx - 2, y + H - 2, z + bz);
    else for (const xx of [x + 6, x + 10, x + 14, x + 18]) this.box(xx, y + 2, z + bz, xx + 1, y + H - 2, z + bz + 1, frame);
  }

  /** Concrete pipe section lying on the ground (hollow, walk-through when r >= 10). */
  pipe(x: number, y: number, z: number, alongX: boolean, len: number, r: number, b: Brush = M('concrete_3')): void {
    const R = Math.ceil(r);
    const cy = y + R;
    for (let a = 0; a < len; a++)
      for (let dy = -R; dy <= R; dy++)
        for (let du = -R; du <= R; du++) {
          const d2 = dy * dy + du * du;
          if (d2 > r * r || d2 < (r - 2.2) * (r - 2.2)) continue;
          if (alongX) this.set(x + a, cy + dy, z + du, b);
          else this.set(x + du, cy + dy, z + a, b);
        }
  }

  /** A-frame cloth tent with an open front (toward +x or +z) and a closed back. */
  tent(x: number, y: number, z: number, alongX: boolean, len: number, width: number, cloth: number): void {
    const h = Math.floor(width / 2) + 1;
    for (let k = 0; k < h; k++) {
      const inset = Math.floor((k * width) / (2 * h));
      const a = inset;
      const b = width - inset;
      if (alongX) {
        this.box(x, y + k, z + a, x + len, y + k + 1, z + Math.min(b, a + 2), cloth);
        this.box(x, y + k, z + Math.max(a, b - 2), x + len, y + k + 1, z + b, cloth);
        this.box(x, y + k, z + a, x + 1, y + k + 1, z + b, cloth);
      } else {
        this.box(x + a, y + k, z, x + Math.min(b, a + 2), y + k + 1, z + len, cloth);
        this.box(x + Math.max(a, b - 2), y + k, z, x + b, y + k + 1, z + len, cloth);
        this.box(x + a, y + k, z, x + b, y + k + 1, z + 1, cloth);
      }
    }
    const ridge = M('log_1');
    const mid = Math.floor(width / 2);
    if (alongX) this.box(x - 1, y + h - 1, z + mid - 1, x + len + 1, y + h + 1, z + mid + 1, ridge);
    else this.box(x + mid - 1, y + h - 1, z - 1, x + mid + 1, y + h + 1, z + len + 1, ridge);
  }

  /** Sandbag wall filling a footprint; rows of 3 voxels alternate inset and shade to look stacked. */
  sandbags(x0: number, z0: number, x1: number, z1: number, y: number, h = 9): void {
    for (let k = 0; k * 3 < h; k++) {
      const inset = k % 2;
      this.box(x0 + inset, y + k * 3, z0 + inset, x1 - inset, y + k * 3 + 3, z1 - inset, inset ? M('sand_2') : M('sand_3'));
    }
  }

  /** Explosive barrel: one connected TNT cluster; the steel bands are rings flush with its surface. */
  tntBarrel(x: number, y: number, z: number): void {
    this.cylinder(x, z, y, y + 9, 3.2, M('tnt'));
    this.cylinder(x, z, y + 2, y + 3, 3.3, M('steel_dark_1'), 2.9);
    this.cylinder(x, z, y + 6, y + 7, 3.3, M('steel_dark_1'), 2.9);
    this.box(x - 1, y + 9, z - 1, x + 1, y + 10, z + 1, M('paint_yellow'));
  }

  /** Explosive crate: a TNT cube with a painted band (the core stays one connected cluster). */
  tntCrate(x: number, y: number, z: number, size = 8): void {
    this.box(x, y, z, x + size, y + size, z + size, M('tnt'));
    const mid = y + (size >> 1);
    this.walls(x, mid - 1, z, x + size, mid + 1, z + size, 1, M('paint_black'));
    this.box(x + 2, y + size, z + 2, x + size - 2, y + size + 1, z + size - 2, M('paint_yellow'));
  }

  /** Hay bale (12 x 8 x 8). */
  haybale(x: number, y: number, z: number, alongX = true): void {
    if (alongX) this.box(x, y, z, x + 12, y + 8, z + 8, M('hay'));
    else this.box(x, y, z, x + 8, y + 8, z + 12, M('hay'));
  }
}

export function isAir(w: VoxelWorld, x: number, y: number, z: number): boolean {
  return w.get(x, y, z) === AIR;
}
